import { getOutlookSpace, getOutlookSpaceAssets, syncAssets } from '../service';
import { loadProject, onAssetsLoaded } from '../actions';
import { orchestrator, mutatorAction, action } from 'satcheljs';
import type { OutlookSpace, OutlookSpaceAsset, ProjectId } from 'owa-timestream-types';
import { setProject, processOutlookSpaceAssetSyncResult } from '../privateActions';
import getProject from '../selectors/getProject';
import logAssetsLoaded from '../utils/logAssetsLoaded';

// Skip first loadProject call on newly created projects since the call to
// createOutlookSpace+timeStreamProjectCreated populates the store with both
// the project and its initial set of assets, in the case of templates, so
// there is no need to actually load the project from the server again.
// With notifications in place we might be asked to reload the project multiple times.
// If that happens while we are already loading the project, schedule a reload.
orchestrator(loadProject, function loadProjectOrchestrator({ projectId }) {
    const { newlyCreated, isLoading } = getProject(projectId);
    const operation = newlyCreated ? loadingSucceeded : isLoading ? requestReload : loadProjectLoop;
    operation(projectId);
});

async function loadProjectLoop(projectId: ProjectId) {
    const { canSync } = getProject(projectId);
    do {
        startLoading(projectId);
        try {
            await (canSync ? deltaSync : fullReload)(projectId);
        } catch (error) {
            loadingFailed(projectId);
        }
    } while (getProject(projectId).pendingLoad);
}

async function fullReload(projectId: ProjectId) {
    const p1 = getOutlookSpace(projectId);
    const p2 = getOutlookSpaceAssets(projectId);

    const outlookSpace = await p1;
    const assets = await p2;
    setProjectAndReportAssetsLoaded(projectId, outlookSpace, assets);
}

async function deltaSync(projectId: ProjectId) {
    const p1 = getOutlookSpace(projectId);
    const p2 = syncAssetsLoop(projectId);

    const outlookSpace = await p1;
    setProject(projectId, outlookSpace);

    await p2;

    setProjectAndReportAssetsLoaded(projectId);
}

async function syncAssetsLoop(projectId: ProjectId) {
    let shouldContinue = true;
    do {
        const { skipToken, deltaToken } = getProject(projectId);
        const syncResult = await syncAssets(projectId, { skipToken, deltaToken, maxPageSize: 100 });
        processOutlookSpaceAssetSyncResult(projectId, syncResult);
        shouldContinue = !syncResult.deltaToken;
    } while (shouldContinue);
}

const startLoading = mutatorAction('startLoading', (projectId: ProjectId) => {
    const project = getProject(projectId);
    project.isLoading = true;
    project.loadingFailed = false;
    project.pendingLoad = false;
});

const loadingSucceeded = mutatorAction('loadingSucceeded', (projectId: ProjectId) => {
    const project = getProject(projectId);
    project.isLoading = false;
    project.loaded = true;
    project.newlyCreated = false;
});

const loadingFailed = mutatorAction('loadingFailed', (projectId: ProjectId) => {
    const project = getProject(projectId);
    project.isLoading = false;
    project.loadingFailed = true;
});

const requestReload = mutatorAction('requestReload', (projectId: ProjectId) => {
    getProject(projectId).pendingLoad = true;
});

const setProjectAndReportAssetsLoaded = action(
    'setProjectAndReportAssetsLoaded',
    (
        projectId: ProjectId,
        outlookSpace?: OutlookSpace,
        outlookSpaceAssets?: OutlookSpaceAsset[]
    ) => ({
        projectId,
        outlookSpace,
        outlookSpaceAssets,
    })
);

orchestrator(setProjectAndReportAssetsLoaded, ({ projectId, outlookSpace, outlookSpaceAssets }) => {
    // In the case of a full reload, update project and assets.
    // In the case of delta sync, these have been done already.
    if (outlookSpace && outlookSpaceAssets) {
        setProject(projectId, outlookSpace, outlookSpaceAssets);
    }

    logAssetsLoaded(projectId);
    onAssetsLoaded(projectId);
    loadingSucceeded(projectId);
});
